import React, { useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../shared/context/auth-context";

const MAX_ATTEMPTS = 5;
const RETRY_MS     = 3000;

// Auto-login runs after the first render, so a missing token alone doesn't
// mean the user is logged out; check the stored session as well.
const hasStoredSession = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("userData"));
    return !!stored?.token && new Date(stored.expiration) > new Date();
  } catch (_) {
    return false;
  }
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
}

const CheckoutSuccess = () => {
  const auth = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  // "checking" | "active" | "pending" | "error"
  const [status, setStatus]       = useState("checking");
  const [paidUntil, setPaidUntil] = useState(null);
  const [error, setError]         = useState(null);

  // Ask the backend to verify the checkout with Stripe and activate the plan.
  // This doesn't depend on the Stripe webhook having arrived.
  useEffect(() => {
    if (!auth.token) return;
    if (!sessionId) {
      setError("No checkout session was found in the link.");
      setStatus("error");
      return;
    }

    let cancelled = false;
    let timer;
    let attempt = 0;

    const confirm = async () => {
      attempt += 1;
      try {
        const res = await fetch("/api/payments/confirm-checkout", {
          method:  "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
          body:    JSON.stringify({ sessionId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Could not verify the payment.");
        if (cancelled) return;

        if (data.isPaid) {
          auth.updateSubscription(true, data.paidUntil);
          setPaidUntil(data.paidUntil);
          setStatus("active");
        } else {
          // Payment still processing on Stripe's side; check again shortly
          setStatus("pending");
          if (attempt < MAX_ATTEMPTS) timer = setTimeout(confirm, RETRY_MS);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setStatus("error");
      }
    };

    confirm();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [auth.token, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loggedOut = !auth.token && !hasStoredSession();

  let icon, message, detail, action;
  if (loggedOut) {
    icon    = "✓";
    message = "Payment received";
    detail  = "Log in to see your subscription.";
    action  = { to: "/auth", label: "Log in" };
  } else if (status === "checking") {
    icon    = "…";
    message = "Confirming your payment";
    detail  = "This only takes a moment.";
  } else if (status === "active") {
    icon    = "✓";
    message = "Subscription activated";
    detail  = `Access to trade signals and reports is unlocked until ${formatDate(paidUntil)}.`;
    action  = { to: "/", label: "Go to Home" };
  } else if (status === "pending") {
    icon    = "…";
    message = "Payment is being processed";
    detail  = "Your plan activates as soon as Stripe confirms the payment. You can check the status under Billing.";
    action  = { to: "/usersettings?tab=billing", label: "Go to Billing" };
  } else {
    icon    = "!";
    message = "We couldn't confirm your subscription";
    detail  = `${error} If you were charged, the plan activates automatically once Stripe notifies us.`;
    action  = { to: "/usersettings?tab=billing", label: "Go to Billing" };
  }

  return (
    <div className="settings-page">
      <div className="settings-section" style={{ textAlign: "center", padding: "40px 28px" }}>
        <div className="settings-success__icon" style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
        <p className="settings-success__msg">{message}</p>
        <p className="settings-success__sub" style={{ marginTop: 8, marginBottom: 24 }}>
          {detail}
        </p>
        {action && (
          <Link to={action.to} className="settings-btn" style={{ display: "inline-block", width: "auto", padding: "10px 32px" }}>
            {action.label}
          </Link>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
